/**
 * R-71 — per-report-type required vs recommended data providers.
 *
 * REQUIRED providers block generation when their outcome is
 * failed | skipped | unavailable | baseline | missing. RECOMMENDED
 * providers emit a wizard warning but do not block.
 *
 * Conservative lists: only providers whose absence produces stub or
 * wholly off-topic report content. Derived SED sections (parcel,
 * regulatory, technology) are omitted — they have no live provider.
 *
 * Naming: audit docs refer to `sentiment-scan`; the codebase
 * orchestrator id is `public-sentiment` (see sedBootstrap
 * SECTION_TO_PROVIDER_ID.publicSentiment). The gate resolves both.
 */
import type { ReportTypeId } from './reportTypes';

export interface ReportProviderRequirements {
  /** Block generation when any of these providers failed or returned baseline. */
  required: readonly string[];
  /** Warn in the wizard; generation proceeds. */
  recommended: readonly string[];
}

export const REPORT_REQUIRED_PROVIDERS = {
  feasibility: {
    required: ['open-meteo', 'hifld-grid', 'usgs-seismic', 'eia-electricity', 'peeringdb-ix'],
    recommended: ['usfws-env', 'airnow-aqi', 'nrel-solar', 'nrel-wind', 'nrc-reactors', 'site-research'],
  },
  environmental: {
    required: ['usfws-env', 'open-meteo', 'airnow-aqi'],
    recommended: ['hifld-grid', 'peeringdb-ix'],
  },
  'test-fit': {
    required: ['usgs-seismic', 'regrid-parcels'],
    recommended: [],
  },
  'power-availability': {
    required: ['hifld-grid', 'eia-electricity', 'nrc-reactors'],
    recommended: ['nrel-solar', 'nrel-wind'],
  },
  'water-resource': {
    required: ['wri-aqueduct', 'open-meteo', 'usfws-env'],
    recommended: [],
  },
  interconnection: {
    required: ['peeringdb-ix'],
    recommended: [],
  },
  'supply-chain-risk': {
    required: ['site-research', 'hifld-grid'],
    recommended: [],
  },
  'public-sentiment': {
    // The sentiment scan AUTO-GATHERS at dispatch (topUpSiteSed →
    // maybeFetchPublicSentiment). A legitimately-quiet site (no media / federal-
    // docket / council-minutes hits — e.g. rural Redhead) yields 0 entries →
    // detectBaselineSentiment flags it 'baseline' → the gate HARD-BLOCKS. That is
    // exactly backwards for a SENTIMENT report ("low public discourse" IS the
    // finding) and violates the codebase's own never-hard-block directive already
    // applied to the tier-1 sentiment providers. Demoted to recommended so the
    // report always dispatches (full when sources have coverage, honest
    // "insufficient data" fallback when they don't). site-research stays required
    // for an evidentiary floor. (golden-pass 2026-06-27.)
    required: ['site-research'],
    recommended: ['public-sentiment'],
  },
  'custom-brief': {
    required: [],
    recommended: ['hifld-grid', 'eia-electricity', 'usfws-env', 'site-research', 'open-meteo', 'peeringdb-ix'],
  },
  'executive-summary': {
    required: [],
    recommended: ['hifld-grid', 'eia-electricity', 'site-research', 'open-meteo', 'usfws-env'],
  },
  // FIN-1 (wf w7nuyquwd) — Financial Viability Report. The body is computed
  // from the deterministic project-finance engine (no live provider), so
  // NOTHING is required; electricity / grid pricing context is recommended.
  financial: {
    required: [],
    recommended: ['eia-electricity', 'hifld-grid', 'site-research'],
  },
} satisfies Record<ReportTypeId, ReportProviderRequirements>;

/** Audit-doc alias for the PSE orchestrator id. */
export const SENTIMENT_SCAN_PROVIDER_ALIASES = ['sentiment-scan', 'public-sentiment'] as const;

export function getReportProviderRequirements(
  reportType: ReportTypeId,
): ReportProviderRequirements {
  return REPORT_REQUIRED_PROVIDERS[reportType];
}

/** Human-readable labels for wizard + gate messages. */
export const PROVIDER_LABELS: Record<string, string> = {
  'open-meteo': 'Climate (Open-Meteo)',
  'hifld-grid': 'Grid (HIFLD)',
  'usgs-seismic': 'Seismic (USGS)',
  'eia-electricity': 'Electricity (EIA)',
  'peeringdb-ix': 'Connectivity (PeeringDB)',
  'usfws-env': 'Environmental (USFWS)',
  'airnow-aqi': 'Air quality (AirNow)',
  'nrel-solar': 'Solar (NREL)',
  'nrel-wind': 'Wind (NREL)',
  'nrc-reactors': 'Nuclear infra (NRC)',
  'wri-aqueduct': 'Water stress (WRI Aqueduct)',
  'regrid-parcels': 'Parcel (Regrid)',
  'site-research': 'Site research (AI)',
  'public-sentiment': 'Public sentiment scan',
  'sentiment-scan': 'Public sentiment scan',
  'external-literature': 'External literature',
  'fema-flood': 'Flood (FEMA)',
};
